/**
 * Het hoofdmenu in stijl A (ontwerp 28-10-2026, `docs/mockups-2026-10/A_jan`):
 * vier groepen in de zijbalk (Overzicht, Ons bezit, Sturen en plannen,
 * Verantwoorden) met daaronder de onderdelen uit de master-architectuur v4.0
 * van Jan. De schermen van een onderdeel staan niet meer in een openklappende
 * zijbalk maar in een tabbalk boven de pagina (`SubNav`). Bij Vastgoed is die
 * tabbalk de niveaubalk rechts in de paginakop (Portefeuille, Deelportefeuille,
 * Complex, Pand, Object), zoals in de mockup; sinds FB-110 ook op een pand- of
 * objectscherm (`components/layout/LevelBar.tsx`).
 *
 * Een scherm dat nog gebouwd moet worden staat grijs met een stip
 * (`built: false`) en komt uit op een placeholder die zegt wat ervoor nodig is
 * (routes.tsx). Bestaande schermen houden hun oude route, zodat bewaarde links
 * blijven werken. `adminOnly` verbergt een ingang voor wie geen beheerder is;
 * de route zelf bewaakt de backend.
 */
import {
  Activity,
  ArrowLeftRight,
  BarChart3,
  Bot,
  Boxes,
  Building,
  Building2,
  Calculator,
  CalendarRange,
  CirclePlus,
  ClipboardCheck,
  CloudSun,
  Compass,
  Database,
  DoorOpen,
  Euro,
  FileBarChart,
  FileCheck2,
  Flame,
  FolderKanban,
  FolderTree,
  Gamepad2,
  Gauge,
  GitCompare,
  Grid3x3,
  HeartPulse,
  History,
  Home,
  Landmark,
  LayoutDashboard,
  MessageSquare,
  Leaf,
  Library,
  LineChart,
  Map,
  PencilRuler,
  Percent,
  Recycle,
  RefreshCw,
  Route,
  Scale,
  Settings,
  Smartphone,
  Store,
  Target,
  TrendingDown,
  TrendingUp,
  Users,
  UsersRound,
  Wallet,
  Wrench,
} from "lucide-react";
import type { ComponentType } from "react";

type Icon = ComponentType<{ size?: number | string; strokeWidth?: number; "aria-hidden"?: boolean }>;

export type NavItem = {
  to: string;
  label: string;
  icon: Icon;
  /** Alleen `/` moet exact matchen; de rest matcht ook op onderliggende routes. */
  end?: boolean;
  /** `false`: nog te bouwen, grijs met een stip in de tabbalk. */
  built?: boolean;
  /** Alleen zichtbaar voor de rol admin (Rekenstatus, Stijlgids). */
  adminOnly?: boolean;
  /** Andere routes die bij dit item horen (detailschermen, oude adressen). */
  also?: string[];
  /**
   * Het label als het item actief is, naar wat er in de URL geselecteerd is;
   * `null` houdt `label`. Voor Omgeving: dezelfde pagina toont een complex of
   * een pand, en de tab moet zeggen welke van de twee (FB-113).
   */
  activeLabel?: (search: URLSearchParams) => string | null;
};

export type NavSection = {
  /** Onderdeel uit v4.0: één regel in de zijbalk. */
  title: string;
  icon: Icon;
  items: NavItem[];
  /** Hoe de schermen van het onderdeel getoond worden; standaard de tabbalk. */
  subnav?: "tabs" | "switch" | "none";
};

export type NavGroup = {
  /** Groepskop in de zijbalk. */
  title: string;
  sections: NavSection[];
};

/**
 * De tab van /omgeving naar de selectie in de URL (FB-113). De pagina leest
 * dezelfde filters (`?building=` voor een pand, `?property_complex=` voor een
 * complex), dus tab en titel zeggen hetzelfde.
 */
export function omgevingLabel(search: URLSearchParams): string | null {
  if (search.get("building")) return "Omgeving pand";
  const complexen = search.getAll("property_complex").filter(Boolean).length;
  if (complexen > 1) return "Omgeving complexen";
  if (complexen === 1) return "Omgeving complex";
  return null;
}

export const navGroups: NavGroup[] = [
  {
    title: "Overzicht",
    sections: [
      {
        title: "Dashboard",
        icon: LayoutDashboard,
        subnav: "none",
        // Opent de laatst gebruikte toegestane sturingslaag (K10): /dashboard stuurt door.
        // De KPI-keten (/kpis) is een doorklik vanuit het dashboard en hoort er
        // in het menu bij, zonder tabbalk (ontwerp 28-10-2026, scherm 39). Het
        // overzicht van wat nog gebouwd wordt ook (scherm 36).
        items: [{ to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, also: ["/kpis", "/nog-te-bouwen"] }],
      },
      {
        title: "Kaart & 3D",
        icon: Map,
        subnav: "none",
        items: [{ to: "/kaart", label: "Kaart & 3D", icon: Map }],
      },
    ],
  },
  {
    title: "Ons bezit",
    sections: [
      {
        title: "Vastgoed",
        icon: Building2,
        subnav: "switch",
        items: [
          { to: "/portefeuille", label: "Portefeuille", icon: Boxes, end: true },
          // Zelfde scherm, andere sectie: de bewaarde selecties staan op /portefeuille.
          { to: "/portefeuille#deelportefeuilles", label: "Deelportefeuilles", icon: FolderTree },
          { to: "/complexen", label: "Complexen", icon: Building2 },
          { to: "/panden", label: "Panden", icon: Building },
          { to: "/objecten", label: "Objecten", icon: Home },
        ],
      },
      {
        title: "Huur & Exploitatie",
        icon: Euro,
        items: [
          { to: "/huur", label: "Huur & WWS", icon: Euro },
          { to: "/huurniveaus", label: "Huurniveaus", icon: BarChart3 },
          { to: "/huurpotentie", label: "Huurpotentie", icon: Percent, built: false },
          { to: "/mutaties", label: "Mutaties", icon: DoorOpen, built: false },
          { to: "/leegstand", label: "Leegstand", icon: Building, built: false },
          { to: "/derving", label: "Derving", icon: TrendingDown, built: false },
        ],
      },
      {
        title: "Omgeving",
        icon: Compass,
        items: [
          { to: "/wijkscan", label: "Gebiedsscan", icon: Compass },
          { to: "/cbs", label: "Demografie", icon: Users },
          { to: "/woningmarkt", label: "Woningmarkt", icon: Store, built: false },
          // FB-052 (Jan, 23-09-2026): Leefbaarometer en veerkracht elk een eigen ingang.
          { to: "/leefbaarheid", label: "Leefbaarheid", icon: HeartPulse },
          { to: "/veerkracht", label: "Veerkracht", icon: Scale },
          // FB-046: de omgeving van een complex of pand; ingang vanuit de dossiers.
          // FB-113 (Jan, 03-11-2026): de tab zegt welke van de twee geselecteerd is.
          { to: "/omgeving", label: "Omgeving complex of pand", icon: Map, activeLabel: omgevingLabel },
          { to: "/klimaat", label: "Klimaat", icon: CloudSun },
          { to: "/beleid", label: "Beleid", icon: Landmark, built: false },
        ],
      },
    ],
  },
  {
    title: "Sturen en plannen",
    sections: [
      {
        title: "Sturing",
        icon: Target,
        items: [
          // Bouwspec KD10a: de koerscyclus; doorrekenen per variant volgt (KD10b).
          { to: "/koers", label: "Koers", icon: Target },
          // Bouwspec KD8: welke KPI's de corporatie volgt, met doelen (wijzigen
          // alleen voor beheer). Heet in de tabbalk "KPI & prestaties", zoals in
          // het ontwerp (scherm 40); de KPI's zelf staan op het dashboard per
          // laag en in de keten (/kpis).
          { to: "/kpi-set", label: "KPI & prestaties", icon: Gauge },
          { to: "/scenarios", label: "Scenario's", icon: GitCompare, built: false },
          // FB-078 deel 3: huidige PMC tegenover de theoretische PMC (begin module 18).
          { to: "/conflictmatrix", label: "Conflicten", icon: Grid3x3 },
          { to: "/planning", label: "Planning & prognose", icon: CalendarRange, built: false },
          { to: "/beleidswaarde", label: "Opbouw beleidswaarde", icon: Calculator },
          { to: "/parameterimpact", label: "Parameterimpact", icon: Route },
        ],
      },
      {
        title: "Programma & Projecten",
        icon: FolderKanban,
        items: [
          // FB-106 (Jan, 28-10-2026): Kans, Programmeren en Impact zijn samen de
          // KPI van PortefeuilleAtlas; elk heet "<KPI> / <game>". De oude naam
          // Ontwerpen / Project-game en zijn adres blijven als doorverwijzing.
          { to: "/wens-game", label: "Kans / Wens-game", icon: Gamepad2, built: false },
          { to: "/ontwerp-game", label: "Programmeren / Ontwerp-game", icon: PencilRuler, built: false, also: ["/project-game"] },
          { to: "/projecten", label: "Projectportfolio", icon: FolderKanban },
          { to: "/portefeuille-game", label: "Impact / Portefeuille-game", icon: ArrowLeftRight, built: false },
          { to: "/sah-potentie", label: "SAH-potentie", icon: Flame },
        ],
      },
      {
        title: "Ingrepen & Lifecycle",
        icon: Activity,
        items: [
          { to: "/lifecycle", label: "Lifecycle", icon: Activity },
          // Plan 20: één invoer voor elke ingreep; het platform verdeelt hem zelf
          // over instandhouding en verbetering (plan 23, besluit I6).
          { to: "/ingrepen/nieuw", label: "Ingrepen", icon: CirclePlus },
          { to: "/conditie", label: "Conditie", icon: ClipboardCheck, built: false },
          { to: "/maatregelen", label: "Maatregelenbibliotheek", icon: Library },
          { to: "/duurzaamheid", label: "Duurzaamheid", icon: Leaf },
        ],
      },
      {
        title: "Begroting",
        icon: Wallet,
        items: [
          { to: "/onderhoud", label: "Instandhouding", icon: Wrench },
          { to: "/verbetering", label: "Verbetering", icon: TrendingUp },
          // Besluit I2 (plan 23): "Desinvestering" is de gebruikersterm.
          { to: "/desinvestering", label: "Desinvestering", icon: Recycle, built: false, also: ["/transformeren"] },
          { to: "/meerjarenbegroting", label: "Meerjarenbegroting", icon: LineChart, built: false },
          { to: "/begroting-vergelijken", label: "Actual · forecast · scenario", icon: GitCompare, built: false },
        ],
      },
    ],
  },
  {
    title: "Verantwoorden",
    sections: [
      {
        title: "Rapportages",
        icon: FileBarChart,
        items: [
          { to: "/rapportages", label: "Rapportages", icon: FileBarChart, built: false },
          { to: "/wsw-aanlevering", label: "WSW/Aw", icon: FileCheck2 },
        ],
      },
      {
        title: "Data & Beheer",
        icon: Database,
        items: [
          { to: "/data-ai", label: "Databronnen & datakwaliteit", icon: Database, built: false },
          { to: "/instellingen", label: "Parameters & rekenregels", icon: Settings },
          // Herberekenen en rekenstatus: alleen voor beheerders (ontwerp 28-10-2026).
          { to: "/rekenstatus", label: "Rekenstatus", icon: RefreshCw, adminOnly: true },
          { to: "/gebruikers", label: "Gebruikers", icon: UsersRound, adminOnly: true },
          { to: "/feedback", label: "Feedback", icon: MessageSquare },
          { to: "/atlas-ai", label: "Atlas AI", icon: Bot, built: false },
          { to: "/audittrail", label: "Audittrail", icon: History, built: false },
        ],
      },
    ],
  },
];

/** De onderdelen plat, in menuvolgorde. */
export const navigation: NavSection[] = navGroups.flatMap((group) => group.sections);

/** Alle menu-items plat, handig voor de router en de placeholder. */
export const allNavItems: NavItem[] = navigation.flatMap((section) => section.items);

/** PortefeuilleAtlas-GO staat onderin de zijbalk, buiten de groepen. */
export const goItem: NavItem = { to: "/go", label: "Atlas-GO", icon: Smartphone, built: false };

/** Of een pad bij een item hoort (zonder de hash; die weegt `isItemActive`). */
export function pathMatchesItem(item: NavItem, pathname: string): boolean {
  const paths = [item.to.split("#")[0], ...(item.also ?? [])];
  return paths.some((path) =>
    item.end ? pathname === path : pathname === path || pathname.startsWith(`${path}/`),
  );
}

/**
 * Of een menu-item bij de huidige locatie hoort. Een item met een `#` (de
 * deelportefeuilles op /portefeuille) is alleen actief bij precies die hash, en
 * het item zonder hash op hetzelfde pad dan juist niet: anders lichten er twee
 * tegelijk op.
 */
export function isItemActive(item: NavItem, pathname: string, hash: string): boolean {
  if (!pathMatchesItem(item, pathname)) return false;
  const [path, itemHash] = item.to.split("#");
  if (itemHash) return hash === `#${itemHash}`;
  const sibling = allNavItems.some(
    (other) => other !== item && other.to.startsWith(`${path}#`) && hash === `#${other.to.split("#")[1]}`,
  );
  return !sibling;
}

/** Het onderdeel waar de huidige locatie onder valt, of `null`. */
export function sectionForPath(pathname: string): NavSection | null {
  return navigation.find((section) => section.items.some((item) => pathMatchesItem(item, pathname))) ?? null;
}

/** De groep waar een onderdeel onder valt (voor het kruimelpad). */
export function groupForSection(section: NavSection): NavGroup | undefined {
  return navGroups.find((group) => group.sections.includes(section));
}
